/**
 * Bot turn orchestration.
 *
 * Owns a bot's whole turn: think (in the worker), act, wait for the world to
 * settle, act again, end the turn. Every game mutation goes through the
 * BotHost's action methods — the same perform* code paths humans and remote
 * peers use — so a bot can never do something a player could not. All waiting
 * goes through the host too, so the game loop decides how time passes.
 *
 * No Three.js, no DOM: this file is plain control flow over GameState.
 */

import { wantsCallIn, commanderMission, dangerClose } from '../core/callIn';
import { wantsMark } from '../core/marks';
import type {
  GameState, PlayerState, BotConfig, BotFogContext, FireParams, JetpackParams, ShopParams,
  CollectActionParams, StructureType, ItemKey, Vec3, RuinState, BotPlanStep,
} from '../types';
import type { BotWorkerClient } from './botWorkerClient';
import type { BotTurnPlan } from './botPlanner';
import {
  chooseBotPostFireAction, computeBotMove, computeBotShop, countEnemyLOS,
  computeAdaptivePressure, applyAdaptivePressure, hasAnyAmmo,
  type BotExposureContext, type BotFireResult, type BotJumpResult, type BotAction, type BotPostFireAction, exploreMove } from './bot';
import { SeededRandom, keyedGenerator } from '../core/random';
import {
  BOT_PRESETS, FIRE_MOVEMENT_COST, JUMP_MIN_COST, MELEE_MOVEMENT_COST, MELEE_RANGE,
  BUILD_MOVEMENT_COST, HEAL_MOVEMENT_COST,
} from '../constants';
import { getPlayerWorldPosition, canPlantFlag } from '../core/gameState';
import { clampMoveTarget } from '../core/actions';
import { losEyePoint } from '../core/los';
import { getRemovableStakes } from '../core/stakes';
import { canCarrierJump } from '../core/carryables';
import { canBotInteract } from './botModeAI';
import { canBuildEcotourism, calculateShopTotalWithFuel } from '../core/economy';
import { chooseHealItem } from '../core/damage';
import { decrementHoldTurns, markCommandExecuted } from '../core/botCommands';
import { getRandomTaunt } from '../lore/flavor';
import { dhypot } from '../core/dmath';
import { chooseTeamPlan, teamRoundValue, orderFor, type TeamCandidate } from './bot/teamPlan';
import type { RoundOrder } from '../core/roundSim';
import { tuningFor } from './bot/tuning';
import { computeTurnOrder } from '../core/gameState';
import { vecLength, vecSub } from '../math';
import { WEAPONS } from '../constants';

/** What a bot turn needs from the game. Implemented by Game in main.ts. */

/**
 * R11 step 6 (the turn bot's keyed draws): every decision in a bot's turn draws from its own stream, keyed by
 * (seed, unit, basis turn, purpose) and a count for the purpose's repeats — so no decision's randomness depends on
 * what another drew before it, and nothing round-trips through the worker. The same stream on every host.
 */
export class TurnDraws {
  private readonly counts = new Map<string, number>();
  constructor(private readonly seed: string | number, private readonly unit: number, private readonly basis: number) {}
  /** A fresh stream for this purpose (its nth use this turn), from the one keyed helper (core/random.ts keyedGenerator). */
  for(purpose: string): SeededRandom {
    const n = this.counts.get(purpose) ?? 0;
    this.counts.set(purpose, n + 1);
    return keyedGenerator(this.seed, this.unit, this.basis, `turnBot-${purpose}-${n}`);
  }
}

export interface BotHost {
  readonly state: GameState;
  readonly useWasm: boolean;
  readonly worker: BotWorkerClient;
  /** Pure-state bookkeeping that is not a player action (command consumption, fire memory). */
  setState(next: GameState): void;
  buildFogContext(player: PlayerState): BotFogContext | undefined;

  showThinking(player: PlayerState): void;
  hideThinking(): void;
  /** Fog-gated log line about this player's action. */
  log(playerId: number, message: string): void;
  /** Harness-only trace of a bot decision (plan chosen, jump step skipped and why). The game leaves it undefined. */
  note?(playerId: number, kind: string, detail: string): void;
  taunt(player: PlayerState, text: string): void;

  // ---- Actions: the same perform* code paths humans and remote peers use ----
  /** X11: ask the command layer for fire on a point (an `order` input of kind callIn through apply). Returns true when answered with a mission. */
  callIn?(planetIndex: number, surfaceDir: Vec3): boolean;
  /** 62.43: the team's seat orders a fire mission for one gun. */
  fireMission?(team: number, gunId: number, planetIndex: number, surfaceDir: Vec3): boolean;
  /** 62.43: the team's seat marks something for the team. */
  mark?(team: number, mark: { planetIndex: number; surfaceDir: Vec3; unitId?: number }): boolean;
  fire(params: FireParams): boolean;
  jump(aim: JetpackParams): void;
  /** Returns true when a walk started. */
  move(targetDir: Vec3): boolean;
  build(structureType: StructureType, surfaceDir: Vec3): void;
  heal(itemKey: ItemKey): void;
  /** Starts a drone delivery, which ends the turn when it lands. */
  shop(params: ShopParams): void;
  melee(targetId: number): void;
  plantFlag(surfaceDir: Vec3): void;
  removeStake(stakeId: number): void;
  pickup(objectId: number): void;
  /** Bots interact with a ruin anywhere on its planet (their planner does not path to it). */
  interact(ruinId?: number): void;
  collect(params: CollectActionParams): void;
  /** Bots deliver anywhere on the destination planet (same reason). */
  deliver(carryableId?: number): void;
  claim(ruinId: number): void;
  /** S6: take an emplacement's seat (Match.mount). Optional: a host without it never seats a bot. */
  mount?(bodyId: number): void;

  // ---- Flow ----
  /** Resolves on the first frame the predicate holds. */
  waitUntil(pred: () => boolean): Promise<void>;
  delay(ms: number): Promise<void>;
  /** Resolves once the bot's shot has fully resolved and the phase is back to 'aiming'. */
  waitForFlightResolution(): Promise<void>;
  /**
   * Round flows (plan & resolve): fire and move are orders that resolve together
   * later, so the shot leaves from where the bot stands now and movement is
   * planned, not walked. The runner shoots first, then spends what is left.
   */
  readonly plannedRound?: boolean;
  isWalking(): boolean;
  endTurn(): void;
}

/** A bot's effective config this turn (preset + adaptive pressure). */
export function botConfigFor(player: PlayerState, state: GameState): BotConfig {
  let config = player.botConfig ?? { skill: 0.5, personality: BOT_PRESETS.balanced };
  if (state.config.adaptiveBotPersonality) {
    const pressure = computeAdaptivePressure(player, state);
    config = { ...config, personality: applyAdaptivePressure(config.personality, pressure) };
  }
  return config;
}

/** Closest living enemy within melee range on the same planet, or null. */
export function findMeleeTarget(player: PlayerState, state: GameState): number | null {
  const playerPos = getPlayerWorldPosition(player, state.planets);
  let closestId: number | null = null;
  let closestDist = MELEE_RANGE;
  for (const other of state.players) {
    if (other.id === player.id || !other.alive) continue;
    if (other.planetIndex !== player.planetIndex) continue;
    if (state.config.friendlyFire === 0 && other.teamIndex === player.teamIndex) continue;
    const o = getPlayerWorldPosition(other, state.planets);
    const dist = dhypot(o.x - playerPos.x, o.y - playerPos.y, o.z - playerPos.z);
    if (dist < closestDist) { closestDist = dist; closestId = other.id; }
  }
  return closestId;
}

const CLAIMABLE_RUIN_TYPES = ['antenna', 'refinery', 'surveyStation', 'habitat', 'supplyCache', 'commsRelay'];

/** A ruin on the player's planet worth claiming (not already claimed by their team). */
export function findClaimableRuin(player: PlayerState, ruins: RuinState[]): RuinState | null {
  return ruins.find(r => {
    if (r.planetIndex !== player.planetIndex) return null;
    if (r.claimTeamIndex !== undefined && r.claimTeamIndex >= 0
        && r.claimTeamIndex === player.teamIndex && r.claimBenefitActive) return false;
    return CLAIMABLE_RUIN_TYPES.includes(r.ruinType);
  }) ?? null;
}

type ModeAction = Exclude<BotAction, 'fire' | 'move' | 'jump' | 'build' | 'shop' | 'skip' | 'heal'>;

export class BotTurnRunner {
  /** 62.43: a unit asks the guns at most once a turn. */
  private askedThisTurn = false;
  constructor(private readonly host: BotHost) {}

  private get state(): GameState { return this.host.state; }
  private player(id: number): PlayerState | undefined { return this.state.players.find(p => p.id === id); }
  private aiming(): boolean { return this.state.phase === 'aiming'; }

  // ===========================================================================
  // ENTRY
  // ===========================================================================

  /** The bot's whole turn, from thinking to endTurn. Safe to call once per bot turn. */
  async runTurn(playerId: number): Promise<void> {
    if (!this.aiming()) return;
    let player = this.player(playerId);
    if (!player || !player.alive) { this.host.endTurn(); return; }

    const config = botConfigFor(player, this.state);
    // R11 step 6: one keyed stream per decision — (seed, unit, basis turn, purpose, count) — nothing carried between them
    const rng = new TurnDraws(this.state.config.seed, playerId, this.state.turnNumber);
    const fog = this.host.buildFogContext(player);

    // Melee first: instant, high damage, no projectile needed.
    if (!this.meleeSweep(playerId)) return;
    player = this.player(playerId)!;

    // 62.43 (lane B's every-switch check): the commander's fire mission — a picture target where the team has nobody
    // goes to its nearest gun (call-ins on); and a mark — an enemy in sight the team has not marked (the picture on)
    if (this.state.config.callIns && this.host.fireMission) {
      const m = commanderMission(this.state, player.teamIndex, fog?.pictureTargets ?? []);
      if (m) { this.host.fireMission(player.teamIndex, m.gunId, m.planetIndex, m.surfaceDir); player = this.player(playerId)!; }
    }
    if (this.state.config.teamPicture && this.host.mark) {
      const mk = wantsMark(this.state, player, fog?.visibleEnemyIds ?? []);
      if (mk) { this.host.mark(player.teamIndex, mk); player = this.player(playerId)!; }
    }
    this.askedThisTurn = false;
    // X11: danger close — an enemy too near to shell without catching itself: ask the guns elsewhere (once a turn)
    if (this.state.config.callIns && this.host.callIn) {
      const close = dangerClose(this.state, player, fog?.visibleEnemyIds ?? [], tuningFor(config).asks.dangerCloseBlasts);
      if (close) { this.askedThisTurn = true; this.host.callIn(close.planetIndex, close.surfaceDir); player = this.player(playerId)!; }
    }

    this.host.showThinking(player);
    let plan: BotTurnPlan;
    try {
      plan = await this.host.worker.computeBotTurnAsync(player, this.state, config, rng.for('plan').getState(), fog);
    } catch {
      this.host.hideThinking();
      this.yieldTurn(player);
      return;
    }
    this.host.hideThinking();
    this.host.note?.(playerId, 'plan', `${plan.plan.debugSource} [${plan.plan.steps.map(s => s.type + ('toward' in s && s.toward ? ':' + s.toward : '')).join(' ')}]${plan.jumpResult ? '' : plan.plan.steps.some(s => s.type === 'jump') ? ' jump:none' : ''}`);
    if (!this.aiming()) return;
    this.consumeCommand(playerId);
    await this.executePlan(playerId, config, plan, fog, rng);
  }

  // ===========================================================================
  // PLAN EXECUTION
  // ===========================================================================

  /**
   * Execute the plan step by step, waiting for each step's animation or
   * resolution. A step that cannot be performed is skipped — or ends the turn
   * if the planner marked it `required`. After the last step the turn ends,
   * unless a step ended it first (post-fire phase, yield, drone delivery).
   */
  private async executePlan(
    playerId: number,
    config: BotConfig,
    plan: BotTurnPlan,
    fog: BotFogContext | undefined,
    rng: TurnDraws,
  ): Promise<void> {
    let precomputedFireUsed = false;
    let jumpUsed = false;
    let fired = false;

    // Round flows: a walk would not change where the queued shot leaves from, and a
    // full walk prices the shot out (FIRE_MOVEMENT_COST) — so shoot first, then move.
    const isFire = (s: BotPlanStep) => s.type === 'fire' || s.type === 'fireProbe';
    const steps = this.host.plannedRound
      ? [...plan.plan.steps.filter(isFire), ...plan.plan.steps.filter(s => !isFire(s))]
      : plan.plan.steps;

    for (const step of steps) {
      const player = this.player(playerId);
      if (!player || !player.alive || !this.aiming()) break;
      let done = false;

      switch (step.type) {
        case 'fire':
        case 'fireProbe': {
          if (player.movementRemaining < FIRE_MOVEMENT_COST) break;
          if (player.holdFire || !hasAnyAmmo(player)) break;

          // The plan's pre-computed shot serves the first non-fresh fire step; a
          // fresh step (after a move/jump) or a second shot is solved from here.
          const fresh = step.type === 'fire' && step.fresh === true;
          let fire: BotFireResult | null = null;
          if (!fresh) {
            fire = !precomputedFireUsed ? plan.fireResult : null;
            precomputedFireUsed = true;
          }
          // Round flows: the team's first planner chooses the team's shots jointly; its own becomes `fire`
          if (this.host.plannedRound && !fresh && step.type === 'fire' && fire && !fog?.round?.plannedShot && tuningFor(config).flow.teamPlan) {
            try { fire = await this.teamPlan(player, config, fire, fog, rng); } catch { /* keep the unit's own shot */ }
            if (!this.aiming()) return;
          }
          if (!fire && step.type === 'fire' && step.yieldIfNoShot && !this.askedThisTurn && this.state.config.callIns && this.host.callIn) {
            this.askedThisTurn = true;
            const ask = wantsCallIn(this.state, player, fog?.visibleEnemyIds ?? [], fog?.pictureTargets ?? []);
            if (ask) this.host.callIn(ask.planetIndex, ask.surfaceDir);
          }
          if (!fire && step.type === 'fire' && step.yieldIfNoShot) {
            // No shot at all. With nobody known (a lone planet, everyone out of sight), walk before yielding so the
            // next look sees new ground — a bot that only stands and yields never finds anyone
            if ((fog?.visibleEnemyIds.length ?? 1) === 0 && player.movementRemaining >= 0.5) {
              const move = exploreMove(player, this.state) ?? computeBotMove(player, this.state, config, rng.for('move'), fog);
              if (move && this.walkToward(player, move.targetDir)) { await this.waitForWalk(); if (!this.aiming()) return; }
            }
            this.yieldTurn(player);
            return;
          }
          if (!fire) {
            try {
              const r = await this.host.worker.computeBotFireAsync(player, this.state, config, rng.for('fire').getState(), fog);
              fire = r.result;
            } catch { break; }
            if (!this.aiming()) return;
          }
          // X11 / 62.43: no shot of its own — ask the guns on another planet for the nearest thing the team knows of (once a turn)
          if (!fire && !this.askedThisTurn && this.state.config.callIns && this.host.callIn) {
            this.askedThisTurn = true;
            const ask = wantsCallIn(this.state, player, fog?.visibleEnemyIds ?? [], fog?.pictureTargets ?? []);
            if (ask) this.host.callIn(ask.planetIndex, ask.surfaceDir);
          }
          if (!fire) break;
          if (!this.fireShot(player, fire, rng.for('taunt'))) break;
          fired = true;
          done = true;
          await this.host.waitForFlightResolution();
          if (!this.aiming()) return;
          break;
        }

        case 'postFire': {
          if (!fired) {
            // No shot found (a lone planet with nobody in sight: blind fire has no enemy planet to aim at) —
            // reposition instead of standing still, so the next look sees something new
            if (player.movementRemaining >= 0.5) {
              const move = computeBotMove(player, this.state, config, rng.for('move'), fog);
              if (move && this.walkToward(player, move.targetDir)) { await this.waitForWalk(); if (!this.aiming()) return; }
            }
            break;
          }
          if (player.movementRemaining <= 0) { this.host.endTurn(); return; }
          await this.postFirePhase(player, config, fog, rng); // ends the turn itself
          return;
        }

        case 'yield':
          this.yieldTurn(player);
          return;

        case 'jump': {
          if (player.movementRemaining < JUMP_MIN_COST) { this.host.note?.(playerId, 'jumpSkipped', `movement ${player.movementRemaining.toFixed(1)} < ${JUMP_MIN_COST}`); break; }
          if (!canCarrierJump(player.id, this.state.carryables, this.state.config.modeConfig)) { this.host.note?.(playerId, 'jumpSkipped', 'carrier may not jump'); break; }
          // The worker already ran the full jump search; a null result means no
          // trajectory exists, so recomputing here would only block rendering.
          const jump = !jumpUsed ? plan.jumpResult : null;
          jumpUsed = true;
          if (!jump || !this.launchJump(playerId, jump)) break;
          done = true;
          await this.waitForLanding(playerId);
          if (!this.aiming()) return;
          if (!this.meleeSweep(playerId)) return; // may have landed next to an enemy
          break;
        }

        case 'mount': {
          // S6: take the seat the plan walked to (Match.mount checks reach, occupancy, movement)
          if (this.host.mount) { this.host.mount(step.structureId); done = true; }
          break;
        }

        case 'move': {
          if (player.movementRemaining < 0.5) break;
          if (!plan.moveResult || !this.walkToward(player, plan.moveResult.targetDir)) break;
          done = true;
          await this.waitForWalk();
          if (!this.aiming()) return;
          break;
        }

        case 'build':
          if (canBuildEcotourism(player.id, this.state.structures)
              && player.movementRemaining >= BUILD_MOVEMENT_COST && player.cbp >= 3) {
            this.host.build('ecotourism', player.surfaceDir);
            done = true;
          }
          break;

        case 'shop':
          if (this.buyDelivery(player, config, rng.for('shop'))) return; // drone delivery ends the turn
          break;

        case 'heal': {
          const key = chooseHealItem(player);
          if (key && player.movementRemaining >= HEAL_MOVEMENT_COST) {
            this.host.heal(key);
            done = true;
          }
          break;
        }

        case 'skip':
          done = true;
          break;

        default:
          this.doModeAction(player, step.type);
          done = true;
          break;
      }

      if (!done) {
        if (step.required) { this.host.endTurn(); return; }
        continue;
      }
      if (step.pauseMs) {
        await this.host.delay(step.pauseMs);
        if (!this.aiming()) return;
      }
    }

    this.host.endTurn();
  }

  // ===========================================================================
  // AFTER A SHOT
  // ===========================================================================

  /** Having fired, decide whether to reposition, jump away, do a mode action, or stay. */
  private async postFirePhase(
    player: PlayerState, baseConfig: BotConfig, fog: BotFogContext | undefined, rng: TurnDraws,
  ): Promise<void> {
    const playerId = player.id;
    // Pressure may have changed since the shot (damage taken, kills)
    const config = this.state.config.adaptiveBotPersonality ? botConfigFor(player, this.state) : baseConfig;
    const fogNow = fog ? this.host.buildFogContext(player) : undefined;

    this.host.showThinking(player);
    const planet = this.state.planets[player.planetIndex];
    const botEye = losEyePoint(player, this.state.planets);
    const enemies = this.state.players.filter(p => p.alive && p.teamIndex !== player.teamIndex);
    const spottingEnemyCount = countEnemyLOS(botEye, player.planetIndex, enemies, this.state.planets, this.host.useWasm);
    const exposure: BotExposureContext = {
      isSpotted: spottingEnemyCount > 0,
      spottingEnemyCount,
      healthFraction: player.health / 100,
      planetDamage: 1 - planet.mass / planet.initialMass,
    };
    const action: BotPostFireAction = chooseBotPostFireAction(player, this.state, config, rng.for('postFire'), exposure, fogNow);
    this.host.hideThinking();

    switch (action) {
      case 'move': {
        const move = computeBotMove(player, this.state, config, rng.for('move'), fogNow, exposure);
        if (move && this.walkToward(player, move.targetDir)) await this.waitForWalk();
        this.host.endTurn();
        return;
      }

      case 'jump': {
        let jump: BotJumpResult | null = null;
        try {
          const r = await this.host.worker.computeBotJumpAsync(player, this.state, config, rng.for('jump').getState(), fogNow);
          jump = r.result;
        } catch { /* no jump — walk instead */ }
        if (!this.aiming()) return;
        if (jump && this.launchJump(playerId, jump)) {
          await this.waitForLanding(playerId);
          if (!this.aiming()) return;
          if (!this.meleeSweep(playerId)) return;
          this.host.endTurn();
          return;
        }
        // No jump found (e.g. carrier on the wrong side): walk toward the target instead
        const fresh = this.player(playerId);
        if (fresh && fresh.movementRemaining >= 0.5) {
          const move = computeBotMove(fresh, this.state, config, rng.for('move'), fogNow, exposure);
          if (move && this.walkToward(fresh, move.targetDir)) await this.waitForWalk();
        }
        this.host.endTurn();
        return;
      }

      case 'skip':
        this.host.endTurn();
        return;

      default:
        this.doModeAction(player, action);
        this.host.endTurn();
        return;
    }
  }

  // ===========================================================================
  // SINGLE ACTIONS
  // ===========================================================================

  /**
   * Team-level plan (ai/bot/teamPlan.ts): for this unit and every bot teammate still to plan this round,
   * gather a few candidate shots (own best; best against another target), choose the combination the
   * round values most, remember the teammates' assignments on them, and return this unit's. Falls back
   * to the unit's own shot when there is nobody to plan with.
   */
  private async teamPlan(player: PlayerState, config: BotConfig, own: BotFireResult, fog: BotFogContext | undefined, rng: TurnDraws): Promise<BotFireResult> {
    const state = this.state;
    const round = fog?.round;
    if (!round) return own;
    const queued = new Set((round.teamOrders ?? []).map(o => o.playerId));
    const order = computeTurnOrder(state.players, state.config.turnOrderMode);
    const cur = order.indexOf(state.currentPlayerIndex);
    const mates = state.players.filter((p, i) => p.alive && p.isBot && p.teamIndex === player.teamIndex && p.id !== player.id && !queued.has(p.id)
      && (round.flow === 'simultaneous' || order.indexOf(i) > cur) && !(p.botPlannedShot && p.botPlannedShot.round === state.turnNumber));
    if (mates.length === 0) return own;
    const enemies = state.players.filter(p => p.alive && p.teamIndex !== player.teamIndex);
    const alternatives = Math.max(0, tuningFor(config).flow.teamPlanAlternatives);
    const nearestEnemy = (pos: Vec3 | undefined) => {
      if (!pos) return null;
      let best: PlayerState | null = null, bd = Infinity;
      for (const e of enemies) { const d = vecLength(vecSub(getPlayerWorldPosition(e, state.planets), pos)); if (d < bd) { bd = d; best = e; } }
      return best;
    };
    const usable = (f: BotFireResult | null): f is BotFireResult => !!f && !WEAPONS[f.weaponKey].hitscan && f.weaponKey !== 'probe';
    const candidates: TeamCandidate[] = [];
    for (const unit of [player, ...mates]) {
      const cfg = unit.id === player.id ? config : botConfigFor(unit, state);
      const ctx = unit.id === player.id ? fog : this.host.buildFogContext(unit);
      const options: BotFireResult[] = [];
      let first: BotFireResult | null = unit.id === player.id ? own : null;
      if (!first) {
        const r = await this.host.worker.computeBotFireAsync(unit, state, cfg, rng.for(`team-${unit.id}`).getState(), ctx);
        first = r.result;
      }
      if (usable(first)) options.push(first);
      // Alternatives: the best shot with the chosen target(s) set aside
      const avoid: number[] = [];
      for (let a = 0; a < alternatives && options.length > 0 && enemies.length > avoid.length + 1 && ctx?.round; a++) {
        const t = nearestEnemy(options[options.length - 1].targetPos);
        if (!t) break;
        avoid.push(t.id);
        const altCtx: BotFogContext = { ...ctx, round: { ...ctx.round, avoidTargetIds: [...(ctx.round.avoidTargetIds ?? []), ...avoid] } };
        const r = await this.host.worker.computeBotFireAsync(unit, state, cfg, rng.for(`team-${unit.id}-alt`).getState(), altCtx);
        if (!usable(r.result)) break;
        const same = options.some(o => { const n = nearestEnemy(o.targetPos); return n && nearestEnemy(r.result!.targetPos)?.id === n.id; });
        if (same) break;
        options.push(r.result);
      }
      if (options.length > 0) candidates.push({ playerId: unit.id, options });
    }
    if (candidates.length < 2) return own;
    const base: RoundOrder[] = (round.teamOrders ?? []).map(o => ({ playerId: o.playerId, fire: o.fire, walk: o.walk, jump: o.jump }));
    const result = chooseTeamPlan(candidates, assignment => {
      const orders = [...base];
      for (const [id, f] of assignment) orders.push(orderFor(id, f));
      return teamRoundValue(state, orders, player.teamIndex);
    });
    const mine = result.assignment.get(player.id) ?? own;
    // Remember the teammates' assignments on them (bot memory, in state: the same on every peer)
    this.host.setState({
      ...this.state,
      players: this.state.players.map(p => {
        const f = p.id !== player.id ? result.assignment.get(p.id) : undefined;
        return f ? { ...p, botPlannedShot: { round: state.turnNumber, fromPlanetIndex: p.planetIndex, fromSurfaceDir: p.surfaceDir, fire: { azimuth: f.azimuth, elevation: f.elevation, power: f.power, weaponKey: f.weaponKey, targetPos: f.targetPos } } } : p;
      }),
    });
    const changed = candidates.filter(c => result.assignment.get(c.playerId) !== c.options[0]).length;
    this.host.note?.(player.id, 'teamPlan', `${candidates.length} units, ${result.evaluated} combinations, value ${result.value.toFixed(0)}, ${changed} changed`);
    if (changed > 0) this.host.log(player.id, `${player.name} plans the team's round: ${changed} of ${candidates.length} shots retargeted`);
    return mine;
  }

  /** Remember the shot for re-engagement, maybe taunt, fire. True when the shot left the barrel. */
  private fireShot(player: PlayerState, fire: BotFireResult, rng: SeededRandom): boolean {
    if (fire.targetPos) {
      this.host.setState({
        ...this.state,
        players: this.state.players.map(p => p.id === player.id
          ? {
            ...p,
            botLastFireParams: {
              azimuth: fire.azimuth, elevation: fire.elevation, power: fire.power, weaponKey: fire.weaponKey,
              fromPlanetIndex: p.planetIndex, fromSurfaceDir: p.surfaceDir,
            },
            botLastFireTargetPos: fire.targetPos,
          }
          : p),
      });
    }
    if (fire.queuePlan) this.host.log(player.id, `${player.name} plans against the queue: ${fire.queuePlan.chose} (${fire.queuePlan.current.toFixed(0)} vs ${fire.queuePlan.predicted.toFixed(0)})`);
    if (rng.next() < 0.3) this.host.taunt(player, getRandomTaunt(player.teamIndex, rng));
    return this.host.fire({ azimuth: fire.azimuth, elevation: fire.elevation, power: fire.power, weaponKey: fire.weaponKey });
  }

  /** Walk as far toward a destination as the movement budget allows. True when a walk started. */
  private walkToward(player: PlayerState, desiredDir: Vec3): boolean {
    const planet = this.state.planets[player.planetIndex];
    const dir = clampMoveTarget(player, planet, desiredDir, this.state.carryables, this.state.config.modeConfig);
    if (!dir) return false;
    return this.host.move(dir);
  }

  /** True when the player actually left the ground. */
  private launchJump(playerId: number, jump: BotJumpResult): boolean {
    const before = this.player(playerId)?.movementRemaining ?? 0;
    this.host.jump({ azimuth: jump.azimuth, elevation: jump.elevation, power: jump.power });
    const after = this.player(playerId);
    if (!after) return false;
    // A planned jump (round flows) is charged now and flown in the round
    return after.airborne === true || (this.host.plannedRound === true && after.movementRemaining < before);
  }

  /** Punch every enemy in reach while movement allows. False when the turn is over (game ended or bot dead). */
  /** Melee anything in reach. False only when the turn is no longer ours (the caller must not end it). */
  private meleeSweep(playerId: number): boolean {
    for (;;) {
      if (!this.aiming()) return false;
      const p = this.player(playerId);
      // Dead (e.g. crash-landed its own jump): the turn is still ours to end. Returning false
      // here left the game waiting on a dead unit's turn forever.
      if (!p || !p.alive) return true;
      if (p.movementRemaining < MELEE_MOVEMENT_COST) return true;
      const target = findMeleeTarget(p, this.state);
      if (target === null) return true;
      this.host.melee(target);
      const after = this.player(playerId);
      if (!after || after.movementRemaining === p.movementRemaining) return this.aiming(); // nothing happened; stop
    }
  }

  /** Buy ammo/fuel if the bot wants to and can afford it. True when a delivery started. */
  private buyDelivery(player: PlayerState, config: BotConfig, rng: SeededRandom): boolean {
    const shop = computeBotShop(player, config, rng, this.state.config.modeConfig.mode, this.state.config.allowedWeapons);
    if (!shop) return false;
    const total = calculateShopTotalWithFuel(shop.purchases, shop.fuelQty);
    if (total <= 0 || total > player.cbp) return false;
    this.host.shop({ purchases: shop.purchases, fuelQty: shop.fuelQty });
    return true;
  }

  /** Mode-specific instant actions (stakes, ruins, carryables). Picks the target the planner meant. */
  private doModeAction(player: PlayerState, action: ModeAction | BotPlanStep['type']): void {
    const s = this.state;
    switch (action) {
      case 'plantStake':
        if (canPlantFlag(player)) this.host.plantFlag(player.surfaceDir);
        return;
      case 'removeStake': {
        const removable = getRemovableStakes(player, s.plantedFlags, s.planets, s.config.modeConfig.stakeRemoveCost);
        if (removable.length > 0) this.host.removeStake(removable[0].id);
        return;
      }
      case 'interact': {
        // The in-range ruin the planner saw (canBotInteract), never one across the planet:
        // an interaction started out of range is cancelled at the round wrap.
        const ruin = canBotInteract(player, s);
        if (ruin) this.host.interact(ruin.id);
        return;
      }
      case 'buildDrill':
        this.host.interact(); // in excavation, interact == build a drill here
        return;
      case 'collect': {
        const c = s.collectibles.find(c => !c.collected && c.planetIndex === player.planetIndex);
        if (c) this.host.collect({ collectibleId: c.id });
        return;
      }
      case 'deliver': {
        const carried = s.carryables.find(c => c.carriedBy === player.id && c.alive);
        if (carried) this.host.deliver(carried.id);
        return;
      }
      case 'pickup': {
        const obj = s.carryables.find(c => c.alive && c.carriedBy === null && c.planetIndex === player.planetIndex
          && !(c.type === 'navCore' && c.teamIndex === player.teamIndex));
        if (obj) this.host.pickup(obj.id);
        return;
      }
      case 'claim': {
        const ruin = findClaimableRuin(player, s.ruins);
        if (ruin) this.host.claim(ruin.id);
        return;
      }
      default:
        return;
    }
  }

  // ===========================================================================
  // WAITING + BOOKKEEPING
  // ===========================================================================

  private async waitForWalk(): Promise<void> {
    await this.host.waitUntil(() => !this.host.isWalking());
    await this.host.delay(300);
  }

  private async waitForLanding(playerId: number): Promise<void> {
    await this.host.waitUntil(() => {
      const p = this.player(playerId);
      return !p || !p.alive || !p.airborne;
    });
    await this.host.delay(300);
  }

  /** A pending leader command has now been considered: clear it (or tick a hold). */
  private consumeCommand(playerId: number): void {
    const p = this.player(playerId);
    if (!p?.pendingCommand) return;
    this.host.setState(p.pendingCommand.type === 'holdPosition'
      ? decrementHoldTurns(this.state, playerId)
      : markCommandExecuted(this.state, playerId));
  }

  private yieldTurn(player: PlayerState): void {
    this.host.log(player.id, `${player.name} yields turn`);
    this.host.endTurn();
  }
}
